/* eslint-disable no-nested-ternary */
import { ReactComponent as Alert } from "../../../assets/svg/alert.svg";
import { OrderTypeResponse, SideResponse } from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { SPACING } from "../../../constants/design/spacing";
import { FormValidatorKeysEnum } from "../../../enums/form";
import {
  alignPriceToStep,
  floorToStepString,
  roundToStepSize,
} from "../../../utils/format";
import { apiNum } from "../../../utils/math";
import { ReduceOnlyInput } from "../form";
import {
  DeselectReduceOnlyContainer,
  InfoRow,
  InputsWrapper,
  Title,
  TradeInfoWrapper,
  Value,
} from "../style";
import FeeInfo from "./FeeInfo";
import { PriceInput, SizeInput } from "./form";
import { OrderProtectionTitle, OrderProtectionWrapper } from "./style";
import { useOptionsTradeFormSetup } from "./useOptionsTradeFormSetup";
import { useOptionsTradeFormComputed } from "./useOptionsTradeFormComputed";
import { useOptionsTradeFormPosition } from "./useOptionsTradeFormPosition";
import { useOptionsTradeFormHandlers } from "./useOptionsTradeFormHandlers";

type SetupResult = ReturnType<typeof useOptionsTradeFormSetup>;
type ComputedResult = ReturnType<typeof useOptionsTradeFormComputed>;
type PositionResult = ReturnType<typeof useOptionsTradeFormPosition>;
type HandlersResult = ReturnType<typeof useOptionsTradeFormHandlers>;

type InputsSectionProps = Pick<SetupResult, "reduceOnly" | "commonFormTranslations" | "orderSide" | "orderType" | "register" | "isLoading" | "setValue" | "errors" | "amount" | "setReduceOnly" | "optionsTradeFormTranslations" | "market"> &
  Pick<ComputedResult, "contractPriceStep" | "currentPosition" | "tradeInfo"> &
  Pick<PositionResult, "reduceOnlyDisabled" | "reduceOnlyNotAllowed" | "insufficientMarketLiquidityWarning"> &
  Pick<HandlersResult, "verifyNotEnoughBalance" | "verifyOrderValueTooSmall" | "maxOrderSizeBeforeOBProtectionTriggered" | "feeStructure">;

export function InputsSection({
  reduceOnlyDisabled,
  reduceOnlyNotAllowed,
  reduceOnly,
  commonFormTranslations,
  orderSide,
  orderType,
  currentPosition,
  contractPriceStep,
  register,
  isLoading,
  setValue,
  errors,
  verifyNotEnoughBalance,
  verifyOrderValueTooSmall,
  amount,
  maxOrderSizeBeforeOBProtectionTriggered,
  insufficientMarketLiquidityWarning,
  setReduceOnly,
  optionsTradeFormTranslations,
  tradeInfo,
  market,
  feeStructure,
}: InputsSectionProps) {
  return (
    <>
      {!reduceOnlyDisabled && (
        <DeselectReduceOnlyContainer
          show={reduceOnlyNotAllowed && reduceOnly}
        >
          <div>{commonFormTranslations("deselect_reduce_only")}</div>
          <div>
            {commonFormTranslations("reduce_only_error_desc_1", {
              orderDirection: commonFormTranslations(
                orderSide.toLowerCase()
              ),
              orderType: commonFormTranslations(orderType),
            })}
            <strong>
              {currentPosition
                ? currentPosition?.side === SideResponse.Buy
                  ? commonFormTranslations(
                      "reduce_only_error_desc_2_open_position_long"
                    )
                  : commonFormTranslations(
                      "reduce_only_error_desc_2_open_position_short"
                    )
                : commonFormTranslations(
                    "reduce_only_error_desc_2_no_position"
                  )}
            </strong>
            {commonFormTranslations("reduce_only_error_desc_3_and")}
            <strong>
              {commonFormTranslations(
                "reduce_only_error_desc_4_reduce_only"
              )}
            </strong>
            {commonFormTranslations("reduce_only_error_desc_5")}
          </div>
        </DeselectReduceOnlyContainer>
      )}
      <InputsWrapper>
        {
          // Only shows price input if limit order.
          orderType === OrderTypeResponse.Limit && (
            <PriceInput
              minPrice={contractPriceStep.price_step}
              register={register("price", {
                disabled: isLoading,
                required: true,
                validate: {
                  [FormValidatorKeysEnum.moreThanZero]: (v) =>
                    parseFloat(v) > 0,
                  // The smallest price is one step, not 1/10^precision: the
                  // precision can be coarser than the market's step.
                  [FormValidatorKeysEnum.decimalsTooSmall]: (v) =>
                    roundToStepSize(
                      v,
                      contractPriceStep.price_step,
                      contractPriceStep.price_precision
                    ) >= contractPriceStep.price_step,
                },
                onBlur(event) {
                  // An empty field stays empty; `.toString()` made it "0" and
                  // wrote tiny prices as "4.5e-7". A buy never pays more than
                  // typed, a sell never sells for less.
                  setValue(
                    "price",
                    alignPriceToStep(
                      event.target.value,
                      contractPriceStep.price_step,
                      contractPriceStep.price_precision,
                      orderSide === SideResponse.Sell ? "up" : "down"
                    )
                  );
                },
              })}
              errors={errors}
            />
          )
        }
        <SizeInput
          minAmountSize={contractPriceStep.amount_step || 0}
          register={register("amount", {
            disabled: isLoading,
            required: true,
            validate: {
              [FormValidatorKeysEnum.moreThanZero]: (v) =>
                parseFloat(v) > 0,
              [FormValidatorKeysEnum.notEnoughBalance]: (v) =>
                verifyNotEnoughBalance(v),
              [FormValidatorKeysEnum.orderValueTooSmall]: (v) =>
                verifyOrderValueTooSmall(v),
              [FormValidatorKeysEnum.decimalsTooSmall]: (v) =>
                roundToStepSize(
                  v,
                  contractPriceStep.amount_step,
                  contractPriceStep.amount_precision
                ) >= contractPriceStep.amount_step,
              [FormValidatorKeysEnum.reduceOnlyOrderSizeValid]: (v) => {
                if (!reduceOnly || reduceOnlyDisabled) {
                  return true;
                }
                return currentPosition
                  ? Number(v || 0) <= Number(currentPosition.amount)
                  : true;
              },
            },
            onBlur(event) {
              const { value } = event.target;
              if (value === "") return;
              // Floored to the step and written at the step's own precision,
              // never in exponent form.
              setValue(
                "amount",
                floorToStepString(
                  value,
                  contractPriceStep.amount_step,
                  contractPriceStep.amount_precision
                )
              );
            },
          })}
          style={{ paddingBottom: 0 }}
          errors={errors}
          warning={
            Number(amount) <= maxOrderSizeBeforeOBProtectionTriggered
              ? insufficientMarketLiquidityWarning
              : undefined
          }
        />
      </InputsWrapper>
      {!reduceOnlyDisabled && (
        <ReduceOnlyInput
          reduceOnly={reduceOnly}
          onToggle={() => setReduceOnly(!reduceOnly)}
        />
      )}
      {Number(amount) > maxOrderSizeBeforeOBProtectionTriggered ? (
        <OrderProtectionWrapper>
          <div>
            <OrderProtectionTitle>
              {optionsTradeFormTranslations("order_protection_triggered")}
              <Alert />
            </OrderProtectionTitle>
          </div>
          <p>
            {/* Protection has been triggered because this order will fill{" "}
                  {orderSide === SideResponse.Buy ? "an offer" : "a bid"} that is
                  outside the order protection barrier. Please{" "} */}
            {orderSide === SideResponse.Buy
              ? optionsTradeFormTranslations("order_protection_desc_bid")
              : optionsTradeFormTranslations(
                  "order_protection_desc_offer"
                )}
            {maxOrderSizeBeforeOBProtectionTriggered > 0 ? (
              <>
                <span>
                  {optionsTradeFormTranslations(
                    "order_protection_desc_reduce_1"
                  )}
                </span>
                {optionsTradeFormTranslations(
                  "order_protection_desc_reduce_2"
                )}
                <span>
                  {optionsTradeFormTranslations(
                    "order_protection_desc_reduce_3",
                    {
                      // Floored: `toFixed` rounded 1.246 up to "1.25", which
                      // still trips the protection check above.
                      amount: floorToStepString(
                        maxOrderSizeBeforeOBProtectionTriggered,
                        contractPriceStep.amount_step,
                        contractPriceStep.amount_precision
                      ),
                    }
                  )}
                </span>
                {optionsTradeFormTranslations(
                  "order_protection_desc_reduce_4"
                )}
              </>
            ) : (
              <span>
                {orderSide === SideResponse.Buy
                  ? optionsTradeFormTranslations(
                      "order_protection_desc_try_limit_order_bid"
                    )
                  : optionsTradeFormTranslations(
                      "order_protection_desc_try_limit_order_ask"
                    )}
              </span>
            )}
          </p>
        </OrderProtectionWrapper>
      ) : (
        <TradeInfoWrapper>
          {tradeInfo.map((info) => (
            <InfoRow
              key={`${info.title}-${info.value}}`}
              warningOrError={info.warningOrError}
            >
              <Title>
                {info.title}
                {Boolean(info.warningOrError && info.showErrorIcon) && (
                  <Alert
                    style={{
                      stroke:
                        info.warningOrError === "warning"
                          ? COLORS.system.one
                          : COLORS.negative.one,
                      marginLeft: `${SPACING.one}px`,
                      marginTop: `-${SPACING.one / 2}px`,
                    }}
                  />
                )}
              </Title>
              <Value>{info.value}</Value>
            </InfoRow>
          ))}

          {/* If market order, show fees */}
          {orderType === OrderTypeResponse.Market && (
            <FeeInfo
              asset={market.asset}
              size={Number(amount || 0)}
              // Null taker fee (no verified schedule): FeeInfo shows "---".
              feeStructure={apiNum(feeStructure?.taker_fee)}
            />
          )}
        </TradeInfoWrapper>
      )}
    </>
  );
}
